import { SagaIterator } from 'redux-saga';
import {
  call,
  put,
  takeEvery
  } from 'redux-saga/effects';
import _ from 'underscore';

import { requestSpaceContents } from '~src/app/actions/spaces/RequestSpaceContentsActions';
import { reloadSpaceIfUpdatingCurrentSpace } from '~src/app/sagas/spaces/content/RefreshContentSagas';
import { createMultipleItemResultNotificationValues, MultipleItemResultNotificationValues } from '~src/commonInfra/contentActions/ActionMessages';
import { ContentActionNotification } from '~src/commonInfra/contentActions/ContentActionNotification';
import { IErrorItem, MultipleItemResult } from '~src/commonInfra/contentActions/MultipleItemResult';
import { CommonErrorCode, DataAccelerationErrorCode } from '~src/ts/helpers/ErrorCodes';
import { ViewContentType } from '~src/ts/models/ContentTypes';
import { IContentInfo } from '~src/ts/models/IContentInfo';
import { Views } from '~src/views/resources/Views';
import { IWorkbookInfo } from '~src/workbook/models/IWorkbookInfo';

import { DataAcceleration } from '../../app/actions/AppActionTypes';
import { requestFailed } from '../../app/actions/RequestFailedAction';
import { Formatters, Messages } from '../../codegen/Localize';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { WorkbookContentType } from '../../ts/models/ContentTypes';
import { Set } from '../../ts/models/Set';
import { DataAccelerationAction, DataAccelerationType } from '../actions/DataAccelerationEntityActions';
import { DataAccelerationState } from '../types/IViewAcceleration';

function processDataAccelerationAction(contents: Set<IContentInfo>, dataAccelerationType: DataAccelerationType): Promise<IResult> {
  if (contents.size() < 1) {
    return null;
  }

  if (dataAccelerationType === DataAccelerationType.turnOnAcceleration) {
    return Views.turnOnDataAccelerationForViews(contents.keys());
  } else {
    return Views.turnOffDataAccelerationForViews(contents.keys());
  }
}

function getViewsForWorkbook(workbook: IWorkbookInfo): Promise<IResult> {
  const viewsQuery = Views.forWorkbook(workbook);

  return viewsQuery.getCount()
    .then((viewCount) => viewsQuery.getSlice(0, viewCount))
    .then((result) => result.items) as Promise<any>;
}

export function showNotification(result: IResult, contents: Set<IContentInfo>, dataAccelerationType: DataAccelerationType) {
  const notification: MultipleItemResultNotificationValues = createMultipleItemResultNotificationValues(
    new MultipleItemResult(contents, result),
    {
      describeSuccessSummary: (successCount: number) => {
        return getSuccessSummary(successCount, dataAccelerationType);
      },
      describeErrorSummary: (errorCount: number) => {
        return getErrorSummary(errorCount, dataAccelerationType);
      },
      describeErrorDetail: (errorItem: IErrorItem<IContentInfo>) => {
        return {
          itemName: errorItem.item.name,
          errorMessage: getErrorMessageFor(errorItem.error.code)
        };
      }
    }
  );
  ContentActionNotification.displayItemizedToastsForResult(notification);
}

export function getSuccessSummary(successCount: number, dataAccelerationType: DataAccelerationType): string {
  if(dataAccelerationType === DataAccelerationType.turnOnAcceleration) {
    return Messages.turnOnDataAccelerationForViewsSuccess({ITEM_COUNT: successCount, ITEM_COUNT_LOC: Formatters.formatCount(successCount)});
  }

  return Messages.turnOffDataAccelerationForViewsSuccess({ITEM_COUNT: successCount, ITEM_COUNT_LOC: Formatters.formatCount(successCount)});
}

export function getErrorSummary(errorCount: number, dataAccelerationType: DataAccelerationType): string {
  if(dataAccelerationType === DataAccelerationType.turnOnAcceleration) {
    return Messages.turnOnDataAccelerationForViewsError({ERROR_COUNT: errorCount, ERROR_COUNT_LOC: Formatters.formatCount(errorCount)});
  }
  return Messages.turnOffDataAccelerationForViewsError({ERROR_COUNT: errorCount, ERROR_COUNT_LOC: Formatters.formatCount(errorCount)});
}

export function getErrorMessageFor(errorCode: number): string {
  switch (errorCode) {
    case DataAccelerationErrorCode.MATERIALIZED_VIEWS_FEATURE_DISABLED:
      return Messages.dataAccelerationFeatureDisabled_errorMessage();
    case DataAccelerationErrorCode.WORKBOOK_NOT_FOUND:
      return Messages.dataAccelerationWorkbookNotFound_errorMessage();
    case DataAccelerationErrorCode.SITE_IS_NOT_ACTIVE:
      return Messages.dataAccelerationSiteIsNotActive_errorMessage();
    case CommonErrorCode.CallingUserInsufficientPermissions:
      return Messages.dataAccelerationUserNotPermitted_errorMessage();
    case DataAccelerationErrorCode.MISSING_CREDENTIALS:
      return Messages.dataAccelerationMissingCredential_errorMessage();
    case DataAccelerationErrorCode.WORKBOOK_TOO_LARGE_TO_MATERIALIZE:
      return Messages.dataAccelerationWorkbookTooLarge_errorMessage();
    case DataAccelerationErrorCode.SITE_IS_NOT_ENABLED_FOR_MATERIALIZED_VIEWS:
      return Messages.dataAccelerationSiteDisabled_errorMessage();
    default:
      return Messages.errorUnknown();
  }
}

export function* handleDataAccelerationAction(action: DataAccelerationAction): SagaIterator {
  try {
    const contentDictionaryByType = _.groupBy(action.selectedContent, c => c.contentType);
    const typeArray = Object.keys(contentDictionaryByType);

    if (typeArray.length === 1 && (typeArray[0] === ViewContentType.name || typeArray[0] === WorkbookContentType.name)) {
      let contentsArray = contentDictionaryByType[typeArray[0]];

      if (typeArray[0] === WorkbookContentType.name){
        if (contentsArray.length === 1) {
          contentsArray = yield call(getViewsForWorkbook, contentsArray[0] as any);
        } else {
          // should never come to here as the action is only visible in workbook space
          return;
        }
      }

      // filter out those items that does not need to take any action
      if (action.dataAccelerationType === DataAccelerationType.turnOnAcceleration) {
        contentsArray = contentsArray.filter(i => i.acceleration === DataAccelerationState.Off);
      } else {
        contentsArray = contentsArray.filter(i => i.acceleration !== DataAccelerationState.Off);
      }

      // take action when there are contents need to take action
      if (contentsArray.length >= 1) {
        const contents = Set.fromIdItemArray(contentsArray);
        const contentsResult = yield call(processDataAccelerationAction, contents, action.dataAccelerationType);
        yield call(showNotification, contentsResult, contents, action.dataAccelerationType);
        const didReload = yield call(reloadSpaceIfUpdatingCurrentSpace, contentsArray);
        if (!didReload) {
          yield put(requestSpaceContents());
        }
      }
    } else if (typeArray.length === 1) {
      yield put(requestFailed(`Data acceleration does not support ${typeArray[0]}.`, Messages.dataAccelerationOnlySupportsWorkbooksAndViews_errorMessage()));
    } else {
      yield put(requestFailed(`Data acceleration only supports one content type in a call.`, Messages.dataAccelerationOnlySupportsOneContentTypeInACall_errorMessage()));
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootDataAccelerationSaga(): SagaIterator {
  yield takeEvery(DataAcceleration, handleDataAccelerationAction);
}
